import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Bot,
  Crosshair,
  Flame,
  ListChecks,
  RefreshCw,
  Sparkles,
  Swords,
  Trophy,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const DIFF_COLORS = {
  Beginner: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  Intermediate: "bg-cyan-500/15 border-cyan-500/30 text-cyan-300",
  Advanced: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  Expert: "bg-rose-500/15 border-rose-500/30 text-rose-300",
  New: "bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300",
};

export default function ZombieMapPage() {
  const { t, lang } = useT();
  const { mapId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadGuide = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await api.get(`/zombies/map/${mapId}/guide`, {
        params: { lang: (lang || "de").startsWith("de") ? "de" : "en", refresh: refresh ? "true" : "false" },
      });
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setNotFound(true);
      } else if (status === 429 && detail?.error === "refresh_rate_limited") {
        toast.error((lang || "").startsWith("de") ? detail.message_de : detail.message_en);
      } else {
        toast.error(
          (lang || "").startsWith("de")
            ? "Guide konnte nicht geladen werden."
            : "Couldn't load guide.",
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [mapId, lang]);

  useEffect(() => {
    loadGuide(false);
  }, [loadGuide]);

  if (notFound) {
    return (
      <AppShell>
        <PageContainer>
          <Link to="/app/zombies" className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft size={14} /> {t("zmap.back")}
          </Link>
          <div className="rounded-3xl border border-white/10 bg-zinc-900/50 p-10 text-center">
            <h2 className="font-display text-2xl font-bold">Map not found</h2>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  const map = data?.map;
  const guide = data?.guide;

  return (
    <AppShell>
      <SeoHead kind="zombie_map" id={mapId} lang={(lang || "de").startsWith("de") ? "de" : "en"} />
      <PageContainer className="pb-12">
        <Link
          to="/app/zombies"
          data-testid="zmap-back"
          className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1.5 mb-4"
        >
          <ArrowLeft size={14} /> {t("zmap.back")}
        </Link>

        {/* Hero */}
        {map ? (
          <div className="relative rounded-3xl overflow-hidden border border-white/5 bg-zinc-900/60 mb-6 animate-fade-up">
            <div className="relative h-56 sm:h-72 overflow-hidden">
              <img src={map.cover} alt={map.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-black/70 border-white/10 text-zinc-100 backdrop-blur">
                    {map.game}
                  </Badge>
                  <Badge className={DIFF_COLORS[map.difficulty] || DIFF_COLORS.Intermediate}>
                    {map.difficulty}
                  </Badge>
                </div>
                <h1
                  className="font-display text-3xl sm:text-5xl font-black tracking-tight"
                  data-testid="zmap-title"
                >
                  {map.name}
                </h1>
                <p className="mt-2 max-w-2xl text-sm sm:text-base text-zinc-300">{map.notes}</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* CTAs */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-8">
          <Link to={`/app/coach?map=${mapId}`}>
            <Button
              className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-11 px-5"
              data-testid="zmap-coach-cta"
            >
              <Bot size={16} className="mr-2" /> {t("zmap.coach_cta")}
            </Button>
          </Link>
          <Link to="/app/teammates?game=Call+of+Duty+Zombies">
            <Button
              variant="outline"
              className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white h-11 px-5"
              data-testid="zmap-squad-cta"
            >
              <Users size={16} className="mr-2" /> {t("zmap.squad_cta")}
            </Button>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            {data?.updated_at && (
              <span
                className="text-[11px] text-zinc-500 font-mono"
                data-testid="zmap-updated-at"
              >
                {t("zmap.updated")}: {formatRelative(data.updated_at, (lang || "de").startsWith("de"))}
              </span>
            )}
            <Button
              variant="ghost"
              disabled={refreshing}
              onClick={() => loadGuide(true)}
              className="rounded-full text-zinc-300 hover:text-neon hover:bg-white/5 h-11 px-4"
              data-testid="zmap-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {t("zmap.refresh")}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-400">
            {t("zmap.loading")}
          </div>
        ) : guide ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            {/* Overview */}
            <Section
              title={t("zmap.section.overview")}
              icon={Sparkles}
              testid="zmap-overview"
              className="lg:col-span-2"
            >
              <p className="text-sm sm:text-base text-zinc-200 leading-relaxed">
                {guide.overview || "—"}
              </p>
            </Section>

            {/* Easter Eggs */}
            <Section title={t("zmap.section.ee")} icon={ListChecks} testid="zmap-ee">
              <OrderedList items={guide.easter_egg_steps} />
            </Section>

            {/* Bosses */}
            <Section title={t("zmap.section.bosses")} icon={Swords} testid="zmap-bosses">
              <BulletList items={guide.boss_tips} accent="rose" />
            </Section>

            {/* Wonder Weapons */}
            <Section title={t("zmap.section.weapons")} icon={Crosshair} testid="zmap-weapons">
              {(guide.wonder_weapons || []).length === 0 ? (
                <p className="text-sm text-zinc-500">—</p>
              ) : (
                <ul className="space-y-3">
                  {guide.wonder_weapons.map((w, i) => (
                    <li key={i} className="rounded-xl bg-zinc-950/70 border border-white/5 p-3">
                      <div className="font-display font-bold text-neon">{w.name}</div>
                      <p className="text-sm text-zinc-300 mt-1">{w.note}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Relics */}
            <Section title={t("zmap.section.relics")} icon={Trophy} testid="zmap-relics">
              {(guide.relics || []).length === 0 ? (
                <p className="text-sm text-zinc-500">—</p>
              ) : (
                <ul className="space-y-3">
                  {guide.relics.map((r, i) => (
                    <li key={i} className="rounded-xl bg-zinc-950/70 border border-white/5 p-3">
                      <div className="font-display font-bold text-amber-300">{r.name}</div>
                      <p className="text-sm text-zinc-300 mt-1">{r.effect}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Strategy */}
            <Section title={t("zmap.section.strategy")} icon={Flame} testid="zmap-strategy" className="lg:col-span-2">
              <BulletList items={guide.strategy} accent="cyan" />
            </Section>
          </div>
        ) : null}
      </PageContainer>
    </AppShell>
  );
}

function formatRelative(iso, isDE) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return isDE ? "gerade eben" : "just now";
    if (diff < 3600) {
      const m = Math.floor(diff / 60);
      return isDE ? `vor ${m} Min` : `${m} min ago`;
    }
    if (diff < 86400) {
      const h = Math.floor(diff / 3600);
      return isDE ? `vor ${h} Std` : `${h} h ago`;
    }
    const d = Math.floor(diff / 86400);
    return isDE ? `vor ${d} Tg` : `${d} d ago`;
  } catch (_e) {
    return null;
  }
}

function Section({ title, icon: Icon, children, testid, className = "" }) {
  return (
    <section
      data-testid={testid}
      className={`rounded-2xl border border-white/5 bg-zinc-900/50 p-5 sm:p-6 animate-fade-up ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-neon/15 border border-neon/30 grid place-items-center text-neon">
          <Icon size={15} />
        </span>
        <h2 className="font-display text-base sm:text-lg font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function OrderedList({ items }) {
  if (!items || items.length === 0) return <p className="text-sm text-zinc-500">—</p>;
  return (
    <ol className="space-y-3">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="w-7 h-7 rounded-full bg-neon/15 border border-neon/30 grid place-items-center text-neon font-mono text-xs font-bold shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <p className="text-sm text-zinc-200 leading-relaxed">{s}</p>
        </li>
      ))}
    </ol>
  );
}

function BulletList({ items, accent = "neon" }) {
  if (!items || items.length === 0) return <p className="text-sm text-zinc-500">—</p>;
  const dotCls = {
    neon: "bg-neon",
    cyan: "bg-cyan-400",
    rose: "bg-rose-400",
  }[accent] || "bg-neon";
  return (
    <ul className="space-y-2.5">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
          <p className="text-sm text-zinc-200 leading-relaxed">{s}</p>
        </li>
      ))}
    </ul>
  );
}
